import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Alert, Checkbox, Group, Loader, ScrollArea, Stack, Text } from '@mantine/core';

import { useAuth } from '@/contexts/AuthContext';
import { useChatMessages, useChatThreads, useSendChatMessage } from '@/hooks/useChat';
import { useConversationAttachments } from '@/hooks/useConversationAttachments';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import { vnextApiClient } from '@/services/vnext-api';
import { ChatThreadKind, messageTimestamp } from '@/types/chat';
import { Composer } from './Composer';
import { MessageAttachment } from './MessageAttachment';
import { MessageBubble } from './MessageBubble';

export interface SourceSetSelection {
  messageIds: Set<string>;
  attachmentIds: Set<string>;
}

interface ConversationViewProps {
  entityType: 'enquiry' | 'report' | 'report_request';
  entityId: string;
  kind: ChatThreadKind;
  canSendPrivate?: boolean;
  /** Admin merged view: fold the internal sibling thread's notes into
   * this (client) thread's listing. */
  mergeInternal?: boolean;
  /** When set (enquiry analyst context), messages + attachment chips become
   *  selectable into the shared source set for extraction. */
  selectable?: boolean;
  selection?: SourceSetSelection;
  /** Toggles the message BODY only — attachments select independently via
   *  their own checkboxes (auto-including them bloated the extraction
   *  prompt for long documents). */
  onToggleMessage?: (messageId: string) => void;
  onToggleAttachment?: (attachmentId: string) => void;
  /** Lifts the resolved threadId so the page can bind ConversationFiles /
   *  attachment uploads to the same thread. */
  onThreadResolved?: (threadId: string | undefined) => void;
}

/**
 * Widened, in-page conversation surface (the EnquiryDetail main column).
 * Composes the same hooks as the aside ChatPanel — creates the thread on the
 * first send/upload (never on mount), polls messages, auto-scrolls — but
 * renders larger, shows attachments inline, and (in selectable mode) lets the
 * analyst tick messages/files into the extraction source set. Distinct from
 * ChatPanel so the aside keeps working.
 */
export function ConversationView({
  entityType,
  entityId,
  kind,
  canSendPrivate = false,
  mergeInternal = false,
  selectable = false,
  selection,
  onToggleMessage,
  onToggleAttachment,
  onThreadResolved,
}: Readonly<ConversationViewProps>) {
  const auth = useAuth();
  const myUserId = auth?.user?.id ?? null;
  const myEmail = auth?.user?.email ?? null;

  const {
    threads,
    isLoading: threadsLoading,
    refetch: refetchThreads,
  } = useChatThreads(entityType, entityId);

  const thread = useMemo(() => threads.find((t) => t.kind === kind) ?? null, [threads, kind]);
  const threadId = thread?.id;

  // The thread is created the first time the user actually writes or
  // uploads. One in-flight creation is shared so a burst of uploads
  // can't create twice.
  const creatingRef = useRef<Promise<string> | null>(null);
  const ensureThread = useCallback(async (): Promise<string> => {
    if (threadId) {
      return threadId;
    }
    creatingRef.current ??= vnextApiClient
      .createChatThread({ entityType, entityId, kind })
      .then(async (created) => {
        await refetchThreads();
        return created.id;
      })
      .finally(() => {
        creatingRef.current = null;
      });
    return creatingRef.current;
  }, [threadId, entityType, entityId, kind, refetchThreads]);

  useEffect(() => {
    onThreadResolved?.(threadId);
  }, [threadId, onThreadResolved]);

  const {
    messages,
    isLoading,
    refetch: refetchMessages,
  } = useChatMessages(threadId, mergeInternal);
  const { send, isSending, error: sendError } = useSendChatMessage(threadId);
  const uploadContext = useMemo(() => ({ entityType, entityId }), [entityType, entityId]);
  const { upload, refetch: refetchAttachments } = useConversationAttachments(
    threadId,
    uploadContext
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  useStickToBottom(scrollRef, messages.length, threadId);

  return (
    <Stack gap="sm" h="100%" style={{ minHeight: 0, flex: 1 }}>
      {isLoading && !messages.length && <Loader size="sm" />}
      {sendError && (
        <Alert color="red" title="Failed to send">
          {sendError.message}
        </Alert>
      )}
      <ScrollArea viewportRef={scrollRef} style={{ flex: 1, minHeight: 0 }} type="auto">
        {messages.length === 0 && !isLoading && (
          <Text c="dimmed" size="sm">
            No messages yet — say hello.
          </Text>
        )}
        {messages.map((m, i) => {
          const isMine = Boolean(
            (myUserId && m.senderUserId === myUserId) ||
            (myEmail && m.senderEmail?.toLowerCase() === myEmail.toLowerCase())
          );
          const prev = messages[i - 1];
          const prevTs = prev ? messageTimestamp(prev) : null;
          const ts = messageTimestamp(m);
          const groupedWithPrev = Boolean(
            prev?.senderEmail === m.senderEmail &&
            prev?.isPrivate === m.isPrivate &&
            prevTs &&
            ts &&
            new Date(prevTs).getMinutes() === new Date(ts).getMinutes() &&
            new Date(prevTs).getHours() === new Date(ts).getHours()
          );
          const attachments = m.attachments ?? [];
          // Internal notes are analyst chatter, not client source
          // material — never offer them to the extraction source set.
          const rowSelectable = selectable && !m.isPrivate;
          return (
            <Stack key={m.id} gap={2}>
              {rowSelectable && (
                <Group justify={isMine ? 'flex-end' : 'flex-start'} px={4}>
                  <Checkbox
                    size="xs"
                    label="Use for extraction"
                    checked={selection?.messageIds.has(m.id) ?? false}
                    onChange={() => onToggleMessage?.(m.id)}
                  />
                </Group>
              )}
              <MessageBubble message={m} isMine={isMine} groupedWithPrev={groupedWithPrev} />
              {attachments.length > 0 && (
                <Stack
                  gap={4}
                  px={4}
                  align={isMine ? 'flex-end' : 'flex-start'}
                  style={{ maxWidth: '78%', alignSelf: isMine ? 'flex-end' : 'flex-start' }}
                >
                  {attachments.map((att) => (
                    <MessageAttachment
                      key={att.id}
                      attachment={att}
                      compact
                      leading={
                        rowSelectable ? (
                          <Checkbox
                            size="xs"
                            checked={selection?.attachmentIds.has(att.id) ?? false}
                            onChange={() => onToggleAttachment?.(att.id)}
                            aria-label={`Use ${att.filename} for extraction`}
                          />
                        ) : undefined
                      }
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          );
        })}
      </ScrollArea>
      <Composer
        canSendPrivate={canSendPrivate}
        onSend={async (body) => {
          await send(
            {
              bodyText: body.bodyText,
              isPrivate: body.isPrivate,
              attachmentIds: body.attachmentIds,
            },
            await ensureThread()
          );
          await Promise.all([refetchMessages(), refetchAttachments()]);
        }}
        isSending={isSending}
        disabled={threadsLoading}
        onUploadAttachment={async (file) => upload(file, await ensureThread())}
      />
    </Stack>
  );
}
